// Секцията „Бакшиш" на хартията на личния отчет и на отчета на заведението.
//
// Без бакшиш (сборът му е 0 или по-старо BE без полетата) НЕ рендира нищо —
// хартията остава точно досегашната. Редовете и редът им повтарят блока
// „БАКШИШ" на POS хартията (BE print payload → FiscalAPI), за да не се
// разминават браузърният и POS печатът; сверката „Общо платено с бакшиш" и
// „<метод> общо" за всеки ненулев метод (в брой, с карта — „за сверка с
// терминала", по банка, по сметка…) е само тук.
//
// Числата са ТОЧНО полетата от BE (`lib/reportTips.ts`) — нищо не се смята.
// Хартията е светла котва (`.report-doc-paper`) — същите `gray-*` класове
// като съседните таблици.

import { useTranslation } from "react-i18next";

import type { PaymentBreakdownItem } from "../../../../../api/reports";
import {
  hasReportTips,
  paidByMethodOf,
  reportTipsOf,
  tipOtherMethodName,
  unlistedOtherTipOf,
  type ReportWithTips,
} from "../../../../../lib/reportTips";
import { ReportAmountToReport } from "./ReportAmountToReport";

type Props = {
  /** `payment_breakdown` — за сверката по метод (без него: брой и карта). */
  report: ReportWithTips & { payment_breakdown?: PaymentBreakdownItem[] | null };
  /**
   * Отчетът на заведението: под таблицата и „Сума за отчитане:" — само тук,
   * защото досега тази хартия няма сума за отчитане (личният отчет я има на
   * старото си място).
   */
  withAmountToReport?: boolean;
};

type TipRow = { key: string; label: string; amount: number; bold?: boolean };

const formatAmount = (amount: number) => amount.toFixed(2);
/** „С карта" — редът ѝ за сверка е с терминала (собствен текст). */
const CARD_PAYMENT_METHOD_ID = 2;

export const ReportTipsSection = ({ report, withAmountToReport = false }: Props) => {
  const { t, i18n } = useTranslation();
  const tips = reportTipsOf(report);
  if (!hasReportTips(tips)) return null;

  const isEnglish = (i18n.language ?? "bg").toLowerCase().startsWith("en");
  // Картата: сумата, „дял за разходи", „дял за персонала". Делът на
  // персонала е „(приспада се)" или „(отделно)" според режима от
  // закриването; без нито един — „(приспада се)" с 0.00.
  const rows: TipRow[] = [
    { key: "card", label: t("reportTips.card"), amount: tips.card },
    { key: "card-expense", label: t("reportTips.cardExpense"), amount: tips.cardExpense },
  ];
  if (tips.cardDeducted !== 0 || tips.cardSeparate === 0) {
    rows.push({
      key: "card-deducted",
      label: t("reportTips.cardDeducted"),
      amount: tips.cardDeducted,
    });
  }
  if (tips.cardSeparate !== 0) {
    rows.push({
      key: "card-separate",
      label: t("reportTips.cardSeparate"),
      amount: tips.cardSeparate,
    });
  }
  for (const otherRow of tips.otherRows) {
    rows.push({
      key: `other-${otherRow.payment_method_id}`,
      label: t("reportTips.other", { method: tipOtherMethodName(otherRow, isEnglish) }),
      amount: Number(otherRow.amount),
    });
  }
  // Бакшиш без разпознаваем метод — само в сбора; редът е и на POS хартията.
  const unlistedOther = unlistedOtherTipOf(tips);
  if (unlistedOther !== 0) {
    rows.push({ key: "other-unlisted", label: t("reportTips.otherUnlisted"), amount: unlistedOther });
  }
  rows.push({ key: "total", label: t("reportTips.total"), amount: tips.total, bold: true });

  return (
    <div className="mb-6" data-testid="report-tips-section">
      <h3 className="text-base font-bold text-gray-800 bg-gray-200 px-2 py-1 mb-2">
        {t("reportTips.section")}
      </h3>
      <table className="w-full border-collapse border border-gray-400 text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-400 px-2 py-1 text-left">
              {t("reportTips.colKind")}
            </th>
            <th className="border border-gray-400 px-2 py-1 text-right">
              {t("reportTips.colAmount")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className={row.bold ? "bg-gray-100 font-bold" : ""}>
              <td className="border border-gray-400 px-2 py-1">{row.label}</td>
              <td className="border border-gray-400 px-2 py-1 text-right">
                {formatAmount(row.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Сверката с касовите бонове и по метод: поръчаното е в таблицата по
          видове плащания, бакшишът — тук, общото — под тях; методите с 0.00
          ги няма. Картата е сверката с терминала. */}
      <div className="mt-2 flex flex-col gap-1 text-sm" data-testid="report-paid-by-method">
        <div className="flex justify-between gap-3">
          <span className="text-gray-600">{t("reportTips.totalPaid")}</span>
          <span className="font-semibold">{formatAmount(tips.totalPaid)}</span>
        </div>
        {paidByMethodOf(report).map((row) => (
          <div key={row.paymentMethodId} className="flex justify-between gap-3">
            <span className="text-gray-600">
              {row.paymentMethodId === CARD_PAYMENT_METHOD_ID
                ? t("reportTips.cardPaid")
                : t("reportTips.methodPaid", {
                    method: isEnglish && row.nameEn ? row.nameEn : row.nameBg,
                  })}
            </span>
            <span className="font-semibold">{formatAmount(row.amount)}</span>
          </div>
        ))}
      </div>

      {withAmountToReport ? (
        <div className="mt-4 border-t-2 border-gray-800 pt-2 text-sm">
          <ReportAmountToReport report={report} />
        </div>
      ) : null}
    </div>
  );
};
